/**
 * What a subtitle key does (1.9.0), shared by the asset Subtitles tab and the sequence editor: pure, so it is unit
 * tested. Null when the key has nothing to act on (the sequence editor then slips a clip with ⌥, and ⌥. as before).
 */
import { mergeCues, newCueAt, nudgeCue, setCueEdge, splitCue, type Cue, type CueEditContext } from '@prism/contracts';
import type { SubtitleKeyAction } from './keys';
import type { CueSelection } from './draftStore';

export interface CueKeyState {
  cues: Cue[];
  selection: CueSelection;
  /** The live playhead, in ticks of media (or sequence) time. */
  playhead: number;
  /** The caret in the selected cue's text area, when it has focus (⌥S splits there). */
  caret?: number | null;
  ctx: CueEditContext;
}

export interface CueKeyResult {
  cues?: Cue[];
  selection?: CueSelection;
  seek?: number;
  /** Play from … to (ticks). */
  play?: { from: number; to: number };
  /** Put the caret in this cue's text (a new cue). */
  focus?: string;
}

const byStart = (a: Cue, b: Cue) => a.startTicks - b.startTicks;

export function cueKeyResult(action: SubtitleKeyAction, s: CueKeyState): CueKeyResult | null {
  const one = s.selection.ids.length === 1 ? s.cues.find((c) => c.id === s.selection.ids[0]) ?? null : null;
  switch (action.action) {
    case 'start-at-playhead':
    case 'end-at-playhead': {
      if (!one) return null;
      const edge = action.action === 'start-at-playhead' ? 'start' : 'end';
      const cues = setCueEdge(s.cues, one.id, edge, s.playhead, s.ctx);
      return { cues: cues === s.cues ? undefined : cues, selection: { ids: [one.id], edge } };
    }
    case 'new-cue': {
      const r = newCueAt(s.cues, s.playhead, s.ctx);
      if (!r) return null;
      return { cues: r.cues, selection: { ids: [r.cue.id], edge: null }, focus: r.cue.id };
    }
    case 'split': {
      if (!one) return null;
      const r = splitCue(s.cues, one.id, s.playhead, s.ctx, { caret: s.caret ?? null });
      if (!r) return null;
      return { cues: r.cues, selection: { ids: [r.second.id], edge: null } };
    }
    case 'merge-next': {
      if (!one) return null;
      const r = mergeCues(s.cues, one.id, s.ctx);
      if (!r) return null;
      return { cues: r.cues, selection: { ids: [r.cue.id], edge: null } };
    }
    case 'nudge': {
      if (!one) return null;
      const edge = s.selection.edge ?? 'start';
      const cues = nudgeCue(s.cues, one.id, edge, action.frames, s.ctx);
      const moved = cues.find((c) => c.id === one.id)!;
      return { cues: cues === s.cues ? undefined : cues, selection: { ids: [one.id], edge }, seek: edge === 'start' ? moved.startTicks : Math.max(moved.startTicks, moved.endTicks - 1) };
    }
    case 'play-cue': {
      const c = one ?? s.cues.find((x) => x.id === s.selection.ids[0]);
      return c ? { play: { from: c.startTicks, to: c.endTicks } } : null;
    }
    case 'previous-cue':
    case 'next-cue': {
      const sorted = [...s.cues].sort(byStart);
      if (!sorted.length) return null;
      const at = one?.startTicks ?? s.playhead;
      const next = action.action === 'next-cue'
        ? sorted.find((c) => (one ? c.startTicks > at || (c.startTicks === at && sorted.indexOf(c) > sorted.indexOf(one)) : c.startTicks > at))
        : [...sorted].reverse().find((c) => (one ? c.startTicks < at || (c.startTicks === at && sorted.indexOf(c) < sorted.indexOf(one)) : c.startTicks < at));
      if (!next) return null;
      return { selection: { ids: [next.id], edge: null }, seek: next.startTicks };
    }
    default:
      return null;
  }
}
